import { inject } from "fw";
import { Store, handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { LocalStorageCache } from "caching";
import { once } from "helpers/once";
import { AtsRepository } from "network/ats-repository";
import { AtsExportTaskInfo, Filter, OrganizationFilter, StatusAggregationItem } from "models/monitor-exports";
import { NestedAggregationItem } from "models/aggregations";
import { CurrentUserStore } from "./current-user";
import { AccountType } from "models/ats-organization";

const DEFAULT_PAGE_SIZE = 20;
const REQUIRED_GRID_FIELDS = "metaData,definition.name,definition.dataType,definition.format,delivery.fileProviderId";
const AGGREGATIONS =
    'terms:metaData.status  date:(metaData.dateCreatedUtc~DATEINTERVAL^"TIMEZONE" terms:metaData.status terms:metaData.organizationId~20 avg:metaData.duration avg:metaData.processingTimePerRecord avg:metaData.scheduleDelay avg:metaData.processingDelay avg:metaData.deliveryDelay avg:metaData.totalDelay avg:metaData.processingTime avg:metaData.totalTimeInQueue)';
const CACHE_LABEL = "ats-monitoring-exports-store";

const FILTER_FIELDS_MAPPING: Map<string, string> = new Map()
    .set("organization", "metaData.organizationId")
    .set("format", "definition.format")
    .set("type", "definition.dataType")
    .set("status", "metaData.status")
    .set("missing", "_missing_")
    .set("range", "metaData.dateCreatedUtc")
    .set("exportFrequency", "metaData.ScheduleInfo.ExportFrequency");

interface MonitorExportsShape {
    currentUserId: string;

    loaded: boolean;
    errorLoading: boolean;
    list: AtsExportTaskInfo[];
    statusCounts: StatusAggregationItem[];
    dateBuckets: NestedAggregationItem[];
    total: number;
    filter: Filter;
    search: string;
    orgFilter: OrganizationFilter;
    sort: string;
    runDateAggInterval: string;
    previousPageToken: string;
    nextPageToken: string;
    currentPreviousPageToken: string;
    currentNextPageToken: string;
    pageSize: number;
    pageNumber: number;
    collapsedItem: string;
}

export class FetchListAction {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public orgFilter: OrganizationFilter = {},
        public sort: string = ""
    ) {}
}

export class ExportsEnsureAction {}
export class ExportsRefreshAction {}
export class ExportsResetAction {}
export class NextPageAction {}
export class PreviousPageAction {}
export class ResendScheduledExportById {
    constructor(public id: string) {}
}

export class CollapseItemAction {
    constructor(public id: string) {}
}

type SavedMonitorExportsState = {
    filter: Filter;
    search: string;
    orgFilter: OrganizationFilter;
    sort: string;
    pageNumber: number;
    pageSize: number;
    runDateAggInterval: string;
    previousPageToken: string;
    nextPageToken: string;
    currentPreviousPageToken: string;
    currentNextPageToken: string;
};

@inject
export class MonitorExportsStore extends Store<MonitorExportsShape> {
    private rerunningAggs: boolean = false;
    private lastFilter: string = null;
    private lastOrgFilter: string = null;

    constructor(
        private currentUser: CurrentUserStore,
        private cache: LocalStorageCache,
        private network: AtsRepository
    ) {
        super();
    }

    defaultFilter() {
        return { missing: "parentId" };
    }

    defaultState() {
        return {
            currentUserId: null,

            loaded: false,
            errorLoading: false,
            list: [],
            statusCounts: [],
            dateBuckets: [],
            total: 0,
            filter: {
                ...this.defaultFilter()
            },
            search: null,
            orgFilter: { type: AccountType.Customer, tags: null },
            sort: "-(metaData.dateCreatedUtc)",
            runDateAggInterval: "1d",
            previousPageToken: null,
            nextPageToken: null,
            currentPreviousPageToken: null,
            currentNextPageToken: null,
            pageSize: DEFAULT_PAGE_SIZE,
            pageNumber: 1,

            collapsedItem: ""
        };
    }

    get cacheKey(): string {
        return `manager-user:${this.state.currentUserId}:${CACHE_LABEL}`;
    }

    saveState() {
        if (this.state.currentUserId == null) return;

        const saveState: SavedMonitorExportsState = {
            filter: this.state.filter,
            search: this.state.search,
            orgFilter: this.state.orgFilter,
            sort: this.state.sort,
            pageNumber: this.state.pageNumber,
            pageSize: this.state.pageSize,
            runDateAggInterval: this.state.runDateAggInterval,
            previousPageToken: this.state.previousPageToken,
            nextPageToken: this.state.nextPageToken,
            currentPreviousPageToken: this.state.currentPreviousPageToken,
            currentNextPageToken: this.state.currentNextPageToken
        };
        this.cache.set<SavedMonitorExportsState>(this.cacheKey, saveState);
    }

    restoreState() {
        if (this.state.currentUserId == null) return;

        const savedState = this.cache.get<SavedMonitorExportsState>(this.cacheKey);

        if (savedState) {
            this.setState((state) => ({
                ...state,
                filter: savedState.filter,
                search: savedState.search,
                orgFilter: savedState.orgFilter,
                sort: savedState.sort,
                pageNumber: savedState.pageNumber || 1,
                pageSize: savedState.pageSize || DEFAULT_PAGE_SIZE,
                runDateAggInterval: savedState.runDateAggInterval || "1h",
                previousPageToken: savedState.previousPageToken,
                nextPageToken: savedState.nextPageToken,
                currentPreviousPageToken: savedState.currentPreviousPageToken,
                currentNextPageToken: savedState.currentNextPageToken
            }));
        }
    }

    @handle(StartAction)
    private handleStartAction(action: StartAction) {
        this.setState(() => ({
            ...this.defaultState(),
            currentUserId: action.user.Id
        }));
    }

    @handle(CollapseItemAction)
    private collapseItemAction(action: CollapseItemAction) {
        this.setState((state) => ({
            ...state,
            collapsedItem: action.id
        }));

        if (action.id) {
            this.loadSingleItemData(action.id);
        }
    }

    @handle(LogoutAction)
    private handleLogoutAction() {
        this.setState(() => this.defaultState());
    }

    @handle(ExportsEnsureAction)
    private async handleAtsOrganizationEnsureAction() {
        await once(CACHE_LABEL, async () => {
            if (this.state.loaded) {
                return;
            }

            this.restoreState();
        });
    }

    @handle(ExportsRefreshAction)
    private handleRefreshAction(action: ExportsRefreshAction) {
        if (!this.state.loaded) return;

        this.setState((state) => ({
            ...state,
            loaded: false
        }));

        this.loadPageList();
    }

    @handle(ExportsResetAction)
    private handleResetAction(action: ExportsResetAction) {
        if (!this.state.loaded) return;

        this.setState(() => {
            return {
                ...this.state,
                filter: this.defaultFilter()
            };
        });

        this.loadPageList();
    }

    @handle(FetchListAction)
    private async handleFetchList(action: FetchListAction) {
        this.setState(() => ({
            ...this.state,
            currentUserId: this.state.currentUserId,
            filter: action.filter,
            search: action.search,
            orgFilter: action.orgFilter,
            sort: action.sort,
            loaded: false,
            errorLoading: false,
            pageNumber: 1,
            previousPageToken: null,
            nextPageToken: null,
            currentPreviousPageToken: null,
            currentNextPageToken: null
        }));

        await this.loadPageList();
    }

    @handle(NextPageAction)
    private async handleNextPageAction(action: NextPageAction) {
        const { nextPageToken, pageSize, pageNumber, total } = this.state;

        if (pageNumber * pageSize > total) return;

        this.setState((state) => ({
            ...state,
            loaded: false,
            pageNumber: pageNumber + 1
        }));

        await this.loadPageData(null, nextPageToken);
    }

    @handle(PreviousPageAction)
    private async handlePreviousPage(s: PreviousPageAction) {
        const { previousPageToken, pageNumber } = this.state;
        if (pageNumber <= 1) return;

        this.setState((state) => ({
            ...state,
            loaded: false,
            pageNumber: pageNumber - 1
        }));

        await this.loadPageData(previousPageToken, null);
    }

    @handle(ResendScheduledExportById)
    private async handleResendScheduledExportById(s: ResendScheduledExportById) {
        if (!s.id) return;

        await this.network.atsExportRetryDelivery(s.id, true);
    }

    private async loadPageList() {
        const { currentPreviousPageToken, currentNextPageToken } = this.state;
        await this.loadPageData(currentPreviousPageToken, currentNextPageToken);
    }

    private async loadPageData(previousPageToken: string, nextPageToken: string) {
        const { pageSize, sort, search, filter, statusCounts } = this.state;
        let { runDateAggInterval } = this.state;
        let newStatusCounts = [];
        let newDateBuckets = [];
        const fieldPaths = `PreviousPageToken,NextPageToken,Total,Results(id,${REQUIRED_GRID_FIELDS})`;
        if (!this.rerunningAggs && !filter.range) runDateAggInterval = "1M";

        try {
            this.setState((state) => ({ ...state, loaded: false, errorLoading: false }));
            const filter = this.getWholeFilter(false);
            const orgFilter = this.getOrgFilter();
            const clientModelResponse = await this.network.atsExportsList(
                this.getWholeFilter(),
                search,
                this.getOrgFilter(),
                sort,
                previousPageToken,
                nextPageToken,
                fieldPaths,
                AGGREGATIONS.replace("DATEINTERVAL", runDateAggInterval).replace("TIMEZONE", this.currentUser.state.timezone),
                false,
                pageSize
            );

            if (!statusCounts || !statusCounts.length || (!filter && !orgFilter) || filter !== this.lastFilter || orgFilter !== this.lastOrgFilter) {
                const clientModelResponseAggs = await this.network.atsExportsList(
                    filter,
                    search,
                    orgFilter,
                    sort,
                    previousPageToken,
                    nextPageToken,
                    fieldPaths,
                    AGGREGATIONS.replace("DATEINTERVAL", runDateAggInterval).replace("TIMEZONE", this.currentUser.state.timezone),
                    true,
                    pageSize
                );
                if (clientModelResponseAggs.Aggregations) {
                    newStatusCounts = clientModelResponseAggs.Aggregations["terms_metaData.status"]?.Items;
                    newDateBuckets = clientModelResponseAggs.Aggregations["date_metaData.dateCreatedUtc"]?.Items;
                }
            } else {
                newStatusCounts = statusCounts;
                newDateBuckets = clientModelResponse?.Aggregations["date_metaData.dateCreatedUtc"]?.Items;
            }

            if (runDateAggInterval == "1d" && newDateBuckets?.length > 40) {
                this.state.runDateAggInterval = "1M";
                this.rerunningAggs = true;
                await this.loadPageData(previousPageToken, nextPageToken);
                this.rerunningAggs = false;
                return;
            } else if (runDateAggInterval == "1d" && newDateBuckets?.length == 1) {
                this.state.runDateAggInterval = "1h";
                this.rerunningAggs = true;
                await this.loadPageData(previousPageToken, nextPageToken);
                this.rerunningAggs = false;
                return;
            } else if (runDateAggInterval == "1M" && newDateBuckets?.length == 1) {
                this.state.runDateAggInterval = "1d";
                this.rerunningAggs = true;
                await this.loadPageData(previousPageToken, nextPageToken);
                this.rerunningAggs = false;
                return;
            } else if (runDateAggInterval == "1h" && newDateBuckets?.length > 24) {
                this.state.runDateAggInterval = "1d";
                this.rerunningAggs = true;
                await this.loadPageData(previousPageToken, nextPageToken);
                this.rerunningAggs = false;
                return;
            }

            this.lastFilter = filter;
            this.lastOrgFilter = orgFilter;

            this.setState((state) => ({
                ...state,
                loaded: true,
                statusCounts: newStatusCounts,
                previousPageToken: clientModelResponse.PreviousPageToken,
                nextPageToken: clientModelResponse.NextPageToken,
                currentPreviousPageToken: previousPageToken,
                currentNextPageToken: nextPageToken,
                list: clientModelResponse.Results,
                dateBuckets: newDateBuckets,
                runDateAggInterval: runDateAggInterval,
                total: clientModelResponse.Total
            }));

            this.saveState();
        } catch (err) {
            console.error(err);
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        }
    }

    private async loadSingleItemData(id: string) {
        try {
            // We are replacing search query with an id string to get a single result
            const clientModelResponse = await this.network.atsExportById(id);
            if (clientModelResponse) {
                this.setState((state) => ({
                    ...state,
                    loaded: true,
                    list: this.state.list.map((task) => {
                        return task.ExportTask.Id === clientModelResponse.Id
                            ? {
                                  ...task,
                                  ExportTask: clientModelResponse
                              }
                            : task;
                    })
                }));
            }
        } catch (err) {
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        }
    }

    private getWholeFilter(withStatus = true) {
        const { filter } = this.state;

        if (!Object.keys(filter).length) return null;

        const filterArgs = [];

        for (const [key, value] of Object.entries(filter)) {
            if (value === undefined || value === null) continue;

            if (!withStatus && key === "status") continue;

            if (Array.isArray(value) && value.length) {
                (value as Array<string>).forEach((element) => {
                    filterArgs.push(`${FILTER_FIELDS_MAPPING.get(key)}:${element}`);
                });
            } else {
                filterArgs.push(`${FILTER_FIELDS_MAPPING.get(key)}:${value}`);
            }
        }

        return filterArgs.join(" AND ");
    }

    private getOrgFilter() {
        const { tags, type } = this.state.orgFilter;

        if (type === null && !tags?.length) return null;

        const filterArgs = [];
        if (type !== null) filterArgs.push(`metaData.type:${type}`);
        if (tags?.length) filterArgs.push(...tags.map((tag) => `tags:("${tag}")`));

        return filterArgs.join(" AND ");
    }
}
